import { View, Text, Tooltip } from "../base";
import { Icon } from "../base/Icon";
import { InputField } from "../base/InputField";
import { DropdownField } from "../base/DropdownField";
import { FieldLabel } from "../base/FieldLabel";
import { Switch } from "../base/Switch";
import { useSTTProviders } from "../../hooks/useSTTProviders";

export interface STTConfigFormData {
  sttModel?: string;
  sttEndpointing?: number;
  sttLanguage?: string;
  sttPrompt?: string;
  audioInputDelay?: number;
  sttNoiseReductionType?: string;
  sttPrewarmModel?: string | null;
  sttAutoSwitch?: boolean;
  sttFilter?: string | null;
  eotThreshold?: number;
  eotTimeoutMs?: number;
  eagerEotThreshold?: number | null;
  vadMode?: "server_vad" | "semantic_vad";
  vadEagerness?: "auto" | "low" | "medium" | "high" | null;
}

export interface STTConfigFormProps {
  config: STTConfigFormData;
  onChange: (key: string) => (value: unknown) => void;
  isAdmin?: boolean;
}

export const STTConfigForm = ({ config, onChange, isAdmin = false }: STTConfigFormProps) => {
  const { getAllModels, getModelByValue, loading } = useSTTProviders();

  if (loading) {
    return (
      <View className="flex items-center justify-center p-4">
        <Text className="text-sm text-gray-500">Loading STT providers...</Text>
      </View>
    );
  }

  const allModels = getAllModels();
  const currentModel = getModelByValue(config.sttModel || "nova-3");
  const capabilities = currentModel?.capabilities;

  // Get models that support prewarming (exclude models with built-in VAD)
  const prewarmableModels = allModels.filter((m) => m.capabilities.supportsPrewarm);

  // Map language codes to display labels
  const languageLabelMap: Record<string, string> = {
    en: "English",
    zh: "Chinese",
    multi: "Multi-Lingual",
    auto: "Multi-Lingual",
  };

  // Get language options dynamically from current model's capabilities
  const languageOptions = capabilities?.languages.map((lang) => ({
    label: languageLabelMap[lang] || lang,
    value: lang,
  })) || [{ label: "English", value: "en" }];

  // Determine prompt label based on model type
  const getPromptLabel = () => {
    if (!capabilities?.supportsPrompt) return null;

    if (capabilities.promptType === "keywords") {
      return (
        <View className="flex items-center gap-1">
          <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">STT Keywords</Text>
          <Tooltip
            content={<View>Enter comma-separated keywords (e.g. "quote, policy, premium") to bias transcription toward those terms.</View>}
            placement="top"
          >
            <Icon
              name="info"
              size={16}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help"
            />
          </Tooltip>
        </View>
      );
    }

    if (capabilities.promptType === "system") {
      return (
        <View className="flex items-center gap-1">
          <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">STT Context Prompt</Text>
          <Tooltip
            content={<View>Enter a short system prompt (e.g. "The user is discussing insurance") to provide context for better recognition.</View>}
            placement="top"
          >
            <Icon
              name="info"
              size={16}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help"
            />
          </Tooltip>
        </View>
      );
    }

    return <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">STT Prompt</Text>;
  };

  return (
    <View className="flex flex-col space-y-4">
      {/* Row 1: Model, Endpointing, Audio Input Delay */}
      <View className="flex flex-row gap-2">
        <DropdownField
          name="sttModel"
          label="STT Model"
          placeholder="Select STT model"
          value={config.sttModel || "nova-3"}
          onChange={onChange("sttModel")}
          options={allModels.map((model) => ({
            label: model.label,
            value: model.value,
          }))}
        />

        {/* Show endpointing for: models without built-in VAD (Nova-3, Deepgram, etc.) OR OpenAI with server_vad (uses silence_duration_ms) */}
        {capabilities?.supportsEndpointing && (!capabilities?.supportsVADMode || config.vadMode === "server_vad") && (
          <InputField
            name="sttEndpointing"
            label="STT Endpointing (ms)"
            placeholder="Enter STT endpointing"
            type="number"
            value={config.sttEndpointing ?? capabilities.defaultEndpointing ?? 300}
            onChange={(value) => onChange("sttEndpointing")(value === "" || value === null || value === undefined ? null : Number(value))}
          />
        )}

        <InputField
          name="audioInputDelay"
          label="Audio Input Delay (ms)"
          placeholder="Enter audio input delay"
          type="number"
          value={config.audioInputDelay ?? 0}
          onChange={(value) => onChange("audioInputDelay")(value === "" || value === null || value === undefined ? null : Number(value))}
        />

        {capabilities?.supportsNoiseReduction && (
          <Switch
            label="Input Audio Noise Reduction"
            labelClassName="w-full"
            className="w-full"
            value={config.sttNoiseReductionType || "near_field"}
            onChange={onChange("sttNoiseReductionType")}
            options={[
              { label: "Near Field", value: "near_field" },
              { label: "Far Field", value: "far_field" },
            ]}
          />
        )}
      </View>

      {/* Row 2: Language, Auto Switch, Prewarm Model, Filter */}
      <View className="flex flex-row gap-2">
        <Switch
          label="STT Language"
          labelClassName="w-full"
          className="w-full"
          value={config.sttLanguage || languageOptions[0]?.value || "en"}
          onChange={onChange("sttLanguage")}
          options={languageOptions}
        />

        {capabilities?.supportsAutoSwitch && (
          <Switch
            label="STT Auto Switch"
            labelClassName="w-full"
            className="w-full"
            value={(config.sttAutoSwitch ? "true" : "false") as string}
            onChange={onChange("sttAutoSwitch")}
            options={[
              { label: "Off", value: "false" },
              { label: "On", value: "true" },
            ]}
          />
        )}
      </View>
      <View className="flex flex-row gap-2">
        {prewarmableModels.length > 0 && (
          <DropdownField
            name="sttPrewarmModel"
            label="STT Prewarm Model"
            placeholder="Select model to prewarm"
            value={config.sttPrewarmModel || ""}
            onChange={onChange("sttPrewarmModel")}
            options={[
              { label: "None", value: "" },
              ...prewarmableModels.map((model) => ({
                label: model.label,
                value: model.value,
              })),
            ]}
          />
        )}

        {isAdmin && capabilities?.supportsFilter && (
          <DropdownField
            name="sttFilter"
            label="STT Filter"
            placeholder="Select audio filter"
            value={config.sttFilter || ""}
            onChange={onChange("sttFilter")}
            options={[
              { label: "None", value: "" },
              { label: "DTLN", value: "dtln" },
              { label: "DeepFilterNet2", value: "deepfilternet2" },
              { label: "Krisp", value: "krisp" },
            ]}
          />
        )}
      </View>

      {/* Row 3: Prompt/Keywords */}
      {capabilities?.supportsPrompt && (
        <View className="flex flex-row gap-2">
          <FieldLabel
            label={getPromptLabel()}
            className="w-full"
          >
            <InputField
              value={config.sttPrompt || ""}
              onChange={onChange("sttPrompt")}
            />
          </FieldLabel>
        </View>
      )}

      {/* Row 4: EOT Configuration (Deepgram Flux only) */}
      {capabilities?.supportsEOT && (
        <View className="flex flex-row gap-2">
          <FieldLabel
            label={
              <View className="flex items-center gap-1">
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">EOT Threshold</Text>
                <Tooltip
                  content={
                    <View>
                      This is the confidence level needed to trigger an end-of-turn event. Lowering the threshold speeds up turn detection but makes it less
                      accurate. Valid Values 0.5 - 0.9. Suggested values: Aggressive: 0.6, Conservative: 0.8
                    </View>
                  }
                  placement="top"
                >
                  <Icon
                    name="info"
                    size={16}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help"
                  />
                </Tooltip>
              </View>
            }
            className="w-full"
          >
            <InputField
              name="eotThreshold"
              placeholder="Enter EOT threshold"
              type="number"
              min={0.5}
              max={0.9}
              step={0.1}
              value={config.eotThreshold ?? capabilities.defaultEOTThreshold ?? 0.8}
              onChange={(value) => onChange("eotThreshold")(value === "" || value === null || value === undefined ? null : Number(value))}
            />
          </FieldLabel>

          <FieldLabel
            label={
              <View className="flex items-center gap-1">
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">EOT Timeout (ms)</Text>
                <Tooltip
                  content={<View>A turn will be finished when this much time has passed after speech, regardless of EoT confidence.</View>}
                  placement="top"
                >
                  <Icon
                    name="info"
                    size={16}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help"
                  />
                </Tooltip>
              </View>
            }
            className="w-full"
          >
            <InputField
              name="eotTimeoutMs"
              placeholder="Enter EOT timeout"
              type="number"
              min={100}
              max={10000}
              step={100}
              value={config.eotTimeoutMs ?? capabilities.defaultEOTTimeoutMs ?? 2500}
              onChange={(value) => onChange("eotTimeoutMs")(value === "" || value === null || value === undefined ? null : Number(value))}
            />
          </FieldLabel>

          {isAdmin && capabilities?.supportsEagerEOT && (
            <FieldLabel
              label={
                <View className="flex items-center gap-1">
                  <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Eager EOT Threshold</Text>
                  <Tooltip
                    content={
                      <View>
                        Sends early Eager End of Turn signals when a turn may be complete, helping reduce latency. Turn this on to get faster responses from the
                        agent. Must be lower than EOT Threshold.
                      </View>
                    }
                    placement="top"
                  >
                    <Icon
                      name="info"
                      size={16}
                      className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help"
                    />
                  </Tooltip>
                </View>
              }
              className="w-full"
            >
              <InputField
                name="eagerEotThreshold"
                placeholder="Enter eager EOT threshold"
                type="number"
                min={0.5}
                max={Math.min(0.9, (config.eotThreshold ?? 0.8) - 0.1)}
                step={0.1}
                value={config.eagerEotThreshold ?? ""}
                onChange={(value) => onChange("eagerEotThreshold")(value === "" || value === null || value === undefined ? null : Number(value))}
              />
            </FieldLabel>
          )}
        </View>
      )}

      {/* Row 5: VAD Configuration (OpenAI models only) */}
      {capabilities?.supportsVADMode && (
        <View className="flex flex-row gap-2">
          <Switch
            label="VAD Mode"
            labelClassName="w-full"
            className="w-full"
            value={config.vadMode || capabilities.defaultVADMode || "server_vad"}
            onChange={onChange("vadMode")}
            options={[
              { label: "Server VAD", value: "server_vad" },
              { label: "Semantic VAD", value: "semantic_vad" },
            ]}
          />
          {capabilities?.supportsVADEagerness && config.vadMode === "semantic_vad" && (
            <Switch
              label="VAD Eagerness"
              labelClassName="w-full"
              className="w-full"
              value={config.vadEagerness || capabilities.defaultVADEagerness || "low"}
              onChange={onChange("vadEagerness")}
              options={[
                { label: "Low", value: "low" },
                { label: "Medium", value: "medium" },
                { label: "High", value: "high" },
              ]}
            />
          )}
        </View>
      )}
    </View>
  );
};
